'use client';

import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { useState, useCallback } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { useAppContext } from 'src/context/app';
import {
  useNotifieringar,
  useNotifieringsÖppnare,
  useNotifieringsInställningar,
} from 'src/actions/notifieringar';

import { NotificationItem } from './notification-item';

import type { Notifiering, NotifieringsFilter } from 'src/actions/notifieringar';

// ----------------------------------------------------------------------

const FLIKAR: { value: NotifieringsFilter; label: string }[] = [
  { value: 'alla', label: 'Alla' },
  { value: 'olasta', label: 'Olästa' },
  { value: 'arkiverade', label: 'Arkiverade' },
];

export type NotificationsDrawerProps = IconButtonProps;

export function NotificationsDrawer({ sx, ...other }: NotificationsDrawerProps) {
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();
  const router = useRouter();
  const { organisation } = useAppContext();

  const [aktivFlik, setAktivFlik] = useState<NotifieringsFilter>('alla');
  const [visaInställningar, setVisaInställningar] = useState(false);

  const {
    notifieringar,
    antalAlla,
    antalOlästa,
    antalArkiverade,
    notifieringarLoading,
    markeraLäst,
    markeraAllaLästa,
    arkivera,
  } = useNotifieringar(aktivFlik);

  // Hämtas först när panelen öppnas — de flesta rör aldrig sina inställningar.
  const { inställningar, växla } = useNotifieringsInställningar(open && visaInställningar);

  const antalFörFlik = (flik: NotifieringsFilter) =>
    flik === 'olasta' ? antalOlästa : flik === 'arkiverade' ? antalArkiverade : antalAlla;

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, nyFlik: NotifieringsFilter) => {
    setAktivFlik(nyFlik);
  }, []);

  /** Markerar läst, byter till notifieringens förening om det behövs och navigerar. */
  const gåTillNotifiering = useNotifieringsÖppnare(markeraLäst);

  const öppnaNotifiering = useCallback(
    async (notifiering: Notifiering) => {
      if (notifiering.länk) {
        onClose();
      }
      await gåTillNotifiering(notifiering);
    },
    [gåTillNotifiering, onClose]
  );

  const renderHead = () => (
    <Box sx={{ py: 2, pr: 1, pl: 2.5, minHeight: 68, display: 'flex', alignItems: 'center' }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifieringar
      </Typography>

      {!!antalOlästa && (
        <Tooltip title="Markera alla som lästa">
          <IconButton color="primary" aria-label="Markera alla som lästa" onClick={markeraAllaLästa}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Inställningar">
        <IconButton
          aria-label="Inställningar för notifieringar"
          onClick={() => setVisaInställningar((visas) => !visas)}
          sx={{ color: visaInställningar ? 'primary.main' : undefined }}
        >
          <Iconify icon="solar:settings-bold-duotone" />
        </IconButton>
      </Tooltip>

      <IconButton
        aria-label="Stäng"
        onClick={onClose}
        sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
      >
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Box>
  );

  const renderInställningar = () => (
    <Collapse in={visaInställningar} unmountOnExit>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.neutral' }}>
        <Typography variant="overline" sx={{ display: 'block', color: 'text.disabled' }}>
          Visa notifieringar om
        </Typography>
        <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column' }}>
          {inställningar.map((rad) => (
            <FormControlLabel
              key={rad.domän}
              label={<Typography variant="body2">{rad.etikett}</Typography>}
              control={
                <Switch
                  size="small"
                  checked={rad.påslagen}
                  onChange={(event) => växla(rad.domän, event.target.checked)}
                />
              }
            />
          ))}
        </Box>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Avstängda domäner slutar skapa nya notifieringar i alla dina föreningar. Redan
          mottagna ligger kvar.
        </Typography>
      </Box>
      <Divider sx={{ borderStyle: 'dashed' }} />
    </Collapse>
  );

  const renderTabs = () => (
    <Tabs variant="fullWidth" value={aktivFlik} onChange={handleChangeTab} indicatorColor="custom">
      {FLIKAR.map((flik) => (
        <Tab
          key={flik.value}
          iconPosition="end"
          value={flik.value}
          label={flik.label}
          icon={
            <Label
              variant={flik.value === aktivFlik ? 'filled' : 'soft'}
              color={
                (flik.value === 'olasta' && 'info') ||
                (flik.value === 'arkiverade' && 'success') ||
                'default'
              }
            >
              {antalFörFlik(flik.value)}
            </Label>
          }
        />
      ))}
    </Tabs>
  );

  const renderTomt = () => (
    <Box sx={{ px: 3, py: 8, textAlign: 'center' }}>
      <Iconify
        width={48}
        icon="solar:bell-off-bold-duotone"
        sx={{ color: 'text.disabled', mb: 1 }}
      />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {aktivFlik === 'olasta'
          ? 'Inget oläst.'
          : aktivFlik === 'arkiverade'
            ? 'Inget arkiverat än.'
            : 'Inga notifieringar än.'}
      </Typography>
    </Box>
  );

  const renderList = () => (
    <Scrollbar>
      {notifieringar.length === 0 && !notifieringarLoading ? (
        renderTomt()
      ) : (
        <Box component="ul">
          {notifieringar.map((notifiering) => (
            <Box component="li" key={notifiering.id} sx={{ display: 'flex' }}>
              <NotificationItem
                notifiering={notifiering}
                valdOrgId={organisation?.id}
                onKlick={öppnaNotifiering}
                onArkivera={arkivera}
              />
            </Box>
          ))}
        </Box>
      )}
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label={
          antalOlästa ? `Notifieringar, ${antalOlästa} olästa` : 'Notifieringar'
        }
        onClick={onOpen}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={antalOlästa} color="error">
          <Iconify width={24} icon="solar:bell-bing-bold-duotone" />
        </Badge>
      </IconButton>

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
          paper: { sx: { width: 1, maxWidth: 420 } },
        }}
      >
        {renderHead()}
        {renderInställningar()}
        {renderTabs()}
        {renderList()}

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="large"
            onClick={() => {
              onClose();
              router.push(paths.dashboard.notifieringar);
            }}
          >
            Visa alla
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
